import { Button, Skeleton, Tooltip, tableHelpers, lucideIcons, toast } from '@round/creatorbase-ui';
import styles from './AddPostCell.module.css';
import { influencer } from '@round/api';
import { CellContext } from '@tanstack/react-table';
import { getTableMeta, Meta } from '../../helpers';
import { postStatusLabels } from 'Modules/Plans/Posts/helpers';
import { ClientInfluencerData } from '../../../helpers';
import { useManageCreatorPosts } from 'Modules/Plans/Project/ProjectDetails/components/AddCreatorsToCampaignModal/useManageCreatorPosts';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { useCampaignStats, useClient } from '@round/creatorbase-helpers/api-hooks';
import { useProjectPostsData } from 'Modules/Plans/Project/ProjectDetails/components/AddCreatorsToCampaignModal/useProjectPostsData';
import { getStatusBadgeClassName } from 'Modules/Plans/Posts/helpers/getPostStatusClassName/getPostStatusClassName';
import cn from 'classnames';

const { Plus, Check, Trash2, InfoIcon } = lucideIcons;

type Props = Pick<
    Meta,
    | 'getCreatorPostData'
    | 'areActionsDisabled'
    | 'isLoading'
    | 'projectId'
    | 'campaign'
    | 'onUpdate'
    | 'addedPostIds'
    | 'onPostCreated'
    | 'onPostDeleted'
> & {
    creatorData: ClientInfluencerData | null;
};

export const AddPostToCampaign = ({
    creatorData,
    getCreatorPostData,
    areActionsDisabled,
    isLoading,
    projectId,
    campaign,
    onUpdate,
    addedPostIds,
    onPostCreated,
    onPostDeleted,
}: Props) => {
    const { selectedClientId } = useClientSelect();
    const { client } = useClient(selectedClientId);

    const { invalidate: invalidateCampaignStats } = useCampaignStats(
        campaign?.id ? { campaign_id: campaign.id } : null,
        {}
    );

    const { refetch: refetchProjectPostsData, addPostToState, removePostFromState } = useProjectPostsData(projectId);

    const creatorPostData = creatorData ? getCreatorPostData(creatorData.clientInfluencerAccountId) : null;

    const { create: createPostMutation, delete: deletePostMutation } = useManageCreatorPosts({
        postId: creatorPostData?.campaignPost?.id,
    });

    const currentPost = creatorPostData?.campaignPost;
    const currentPostStatus = currentPost?.status;
    const wasAddedInThisSession = !!currentPost && addedPostIds.has(currentPost.id);

    const handleCreatePost = async (status: 'planned' | 'backup') => {
        if (!creatorData || !campaign?.id) {
            return;
        }

        const costFields = (() => {
            if (!campaign.is_post_cost_editable) {
                return undefined;
            }

            if (client?.is_payment_enabled) {
                return { base_cost: creatorData.cost, currency_id: creatorData.currencyId };
            }

            if (creatorData.currencyId === campaign.currency_id) {
                return { cost: creatorData.cost };
            }

            return { cost: null };
        })();

        createPostMutation.mutate(
            {
                data: {
                    campaign_id: campaign.id,
                    client_influencer_account_id: creatorData.clientInfluencerAccountId,
                    ...costFields,
                },
                status,
            },
            {
                onSuccess: (post) => {
                    toast.success('Creator added');

                    onPostCreated(post.id);

                    // Update the local state to display the new post immediately.
                    addPostToState(post);

                    // Invalidate/refresh the queries to ensure fresh data.
                    refetchProjectPostsData();
                    invalidateCampaignStats(campaign.id);
                    onUpdate();
                },
                onError: () => {
                    toast.error(`Could not add creator${status === 'backup' ? ' as backup' : ''}`);
                },
            }
        );
    };

    const handleDeletePost = async () => {
        if (!currentPost) {
            return;
        }

        deletePostMutation.mutate(currentPost.id, {
            onSuccess: () => {
                toast.info('Post deleted');
                if (campaign?.id) {
                    invalidateCampaignStats(campaign.id);
                }

                onPostDeleted(currentPost.id);

                // Update the local state to remove the post immediately.
                removePostFromState(currentPost.id);
                // Invalidate/refresh the queries to ensure fresh data.
                refetchProjectPostsData();
                onUpdate();
            },
            onError: () => {
                toast.info('Could not delete post');
            },
        });
    };

    return (
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
            {isLoading ? (
                <>
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                </>
            ) : (
                <>
                    {!!creatorPostData?.otherCampaignsWithCreator?.length && (
                        <Tooltip delayDuration={100}>
                            <Tooltip.Trigger asChild>
                                <InfoIcon className={styles.infoIcon} />
                            </Tooltip.Trigger>
                            <Tooltip.Content side="top">
                                <span className={styles.campaignListTitle}>Already in campaigns:</span>
                                <ul className={styles.campaignList}>
                                    {creatorPostData?.otherCampaignsWithCreator?.map((c) => (
                                        <li key={c.id} className={styles.campaignListItem}>
                                            {c.name}
                                        </li>
                                    ))}
                                </ul>
                            </Tooltip.Content>
                        </Tooltip>
                    )}
                    {!!currentPostStatus ? (
                        <>
                            <div
                                className={cn(
                                    styles.addedBadge,
                                    getStatusBadgeClassName(currentPostStatus),
                                    styles[currentPostStatus]
                                )}
                            >
                                <Check className={styles.icon} />
                                {postStatusLabels[currentPostStatus]}
                            </div>

                            {!!wasAddedInThisSession && (
                                <Button
                                    variant="destructive"
                                    size="xs"
                                    className={styles.button}
                                    onClick={handleDeletePost}
                                    disabled={deletePostMutation.isLoading || !!areActionsDisabled}
                                >
                                    <Trash2 className={styles.icon} />
                                    Remove
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Button
                                variant="default"
                                size="xs"
                                className={styles.button}
                                onClick={() => handleCreatePost('planned')}
                                disabled={createPostMutation.isLoading || !!areActionsDisabled || !creatorData}
                            >
                                <Plus className={styles.icon} />
                                Planned
                            </Button>

                            <Button
                                variant="outline"
                                size="xs"
                                className={cn(styles.button, styles.backupButton)}
                                onClick={() => handleCreatePost('backup')}
                                disabled={createPostMutation.isLoading || !!areActionsDisabled || !creatorData}
                            >
                                <Plus className={styles.icon} />
                                Backup
                            </Button>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

// Wrapper to map props directly from the table context.
// This stabilises the props passed to the AddPostToCampaign component.
export function AddPostCell({
    table,
    row: { original },
}: CellContext<tableHelpers.WithLoadingRow<influencer.CreatorSearchResult>, unknown>) {
    const {
        getCreatorPostData,
        platform,
        projectId,
        campaign,
        areActionsDisabled,
        isLoading,
        isLoadingPostData,
        onUpdate,
        addedPostIds,
        onPostCreated,
        onPostDeleted,
    } = getTableMeta(table);

    const accountId = platform
        ? original.platforms?.[platform]?.[0]?.creatorbase_client_influencer_account_id
        : undefined;

    return (
        <AddPostToCampaign
            creatorData={
                !!accountId && !!platform
                    ? {
                          clientInfluencerAccountId: accountId,
                          cost: original.platforms?.[platform]?.[0]?.cost ?? null,
                          currencyId: original.platforms?.[platform]?.[0]?.currency_id ?? null,
                      }
                    : null
            }
            getCreatorPostData={getCreatorPostData}
            areActionsDisabled={areActionsDisabled}
            isLoading={isLoading || !!isLoadingPostData}
            projectId={projectId}
            campaign={campaign}
            onUpdate={onUpdate}
            addedPostIds={addedPostIds}
            onPostCreated={onPostCreated}
            onPostDeleted={onPostDeleted}
        />
    );
}
